import {
  DropdownMenuInternalProps,
  DropdownMenuPosition,
  DropdownMenuProps
} from './interface';
import { RefObject, useEffect, useState } from 'react';
import {
  getCalculatedPosition,
  handlePrevOrNextItemFocus
} from './utils/dropdownmenu-logic';

import { Devices } from '../../service/Devices';
import { DropdownMenuContext } from './context';
import { Icon } from '../jdsicon/generated';
import { JDSDropdownMenuGroupProps } from '../generatedIndex';
import { useRootRef } from '../../hooks/useRootRef';
import { useScrollClose } from '../../hooks/useScrollClose';

const MENU_MARGIN = 4; // xxs
export const getIcon = (icon?: string | JSX.Element) => {
  if (!icon) return undefined;
  if (typeof icon === 'string') return <Icon ic={icon} color='grey-80' />;
  if (icon.props?.ic) return <Icon ic={icon.props.ic} color='grey-80' />;
  return <Icon ic={icon} color='grey-80' />;
};
export interface JDSDropdownMenuProps {
  /**  */
  searchTrigger?: boolean;
  /**  */
  labelTextWrap?: boolean;
  /**  */
  open?: boolean;
  /**  */
  width?: 'max-content' | 'trigger';
  /**  */
  position?:
    | 'bottom_start'
    | 'bottom_end'
    | 'top_start'
    | 'top_end'
    | 'right_start'
    | 'right_end'
    | 'left_start'
    | 'left_end'
    | 'bottom_centre'
    | 'top_centre';
  /**  */
  searchLabel?: string;
  /**  */
  searchValue?: string;
  /**  */
  searchSuffix?: string | JSX.Element;
  /**  */
  offsetX?: string | number;
  /**  */
  offsetY?: string | number;
  /**
   * The target element underneath which the menu should open from.
   */
  anchorEl?: RefObject<HTMLElement>;
  onSuffixClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  onSearchChange?: () => void;
  onClose?: () => void;
  children?: React.ReactElement<JDSDropdownMenuGroupProps>;
  custom?: string;
}

export function getPositionCoordinates(
  position: DropdownMenuPosition,
  anchorRect: DOMRect,
  containerDomRect: DOMRect
): {
  left: number;
  top: number;
  maxHeight: number;
  maxWidth: number;
} {
  const {
    top: anchorTop,
    bottom: anchorBottom,
    right: anchorRight,
    left: anchorLeft,
    width: anchorWidth
  } = anchorRect;

  const { width: containerWidth, height: containerHeight } = containerDomRect;

  const screenHeight: number = window.innerHeight;
  const screenWidth: number = window.innerWidth;

  let left = 0;
  let top = 0;
  let maxHeight;
  let maxWidth;
  switch (position) {
    case 'bottom-start':
      left = anchorLeft;
      top = anchorBottom;
      maxHeight = screenHeight - anchorBottom;
      maxWidth = screenWidth - anchorLeft;
      break;
    case 'bottom-end':
      left = anchorRight - containerWidth;
      top = anchorBottom;
      maxHeight = screenHeight - anchorBottom;
      maxWidth = anchorRight;
      break;
    case 'top-start':
      left = anchorLeft;
      top = anchorTop - containerHeight;
      maxHeight = anchorTop;
      maxWidth = screenWidth - anchorLeft;
      break;
    case 'top-end':
      left = anchorRight - containerWidth;
      top = anchorTop - containerHeight;
      maxHeight = anchorTop;
      maxWidth = anchorRight;
      break;
    case 'right-end':
      left = anchorRight;
      top = anchorBottom - containerHeight;
      maxHeight = anchorBottom;
      maxWidth = screenWidth - anchorRight;
      break;
    case 'right-start':
      left = anchorRight;
      top = anchorTop;
      maxHeight = screenHeight - anchorTop;
      maxWidth = screenWidth - anchorRight;
      break;
    case 'left-end':
      left = anchorLeft - containerWidth;
      top = anchorBottom - containerHeight;
      maxHeight = anchorBottom;
      maxWidth = anchorLeft;
      break;
    case 'left-start':
      left = anchorLeft - containerWidth;
      top = anchorTop;
      maxHeight = screenHeight - anchorTop;
      maxWidth = anchorLeft;
      break;
    case 'top-center':
      left = anchorLeft + anchorWidth / 2 - containerWidth / 2;
      top = anchorTop - containerHeight;
      maxHeight = anchorTop;
      maxWidth = screenWidth;
      break;
    case 'bottom-center':
      left = anchorLeft + anchorWidth / 2 - containerWidth / 2;
      top = anchorBottom;
      maxHeight = screenHeight - anchorBottom;
      maxWidth = screenWidth;
      break;
  }

  if (position.includes('top')) {
    top -= MENU_MARGIN;
  } else if (position.includes('bottom')) {
    top += MENU_MARGIN;
  } else if (position.includes('right')) {
    left += MENU_MARGIN;
  } else if (position.includes('left')) {
    left -= MENU_MARGIN;
  }

  return {
    left: Math.round(left < 0 ? 0 : left),
    top: Math.round(top < 0 ? 0 : top),
    maxHeight,
    maxWidth
  };
}

const DROPDOWNMENU_SELECTOR = 'div.j-JDSDropdownMenuInternal-menu-container';
const SEARCH_BAR_HEIGHT = 56;

export const withLogic =
  (
    Component: React.ComponentType<DropdownMenuProps>
  ): React.FC<DropdownMenuProps> =>
  (props: DropdownMenuProps) => {
    const [activeMenuId, setActiveMenuId] = useState<string | undefined>(
      undefined
    );
    const { element, rootRef } = useRootRef();

    const [labelTextWrap, setLabelTextWrap] = useState<boolean>(true);
    const [selectedValue, setSelectedValue] = useState<string | undefined>(
      undefined
    );
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    const [menuVisible, setMenuVisible] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);
    const [isNested, setIsNested] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [menuWidth, setMenuWidth] = useState('max-content');

    const internalProps: DropdownMenuInternalProps = props;

    const { anchorEl, position } = props;

    const dropdownMenuRef = element?.querySelector<HTMLDivElement>(
      DROPDOWNMENU_SELECTOR
    );

    const [coords, setCoords] = useState<{
      left: number;
      top: number;
      maxHeight: number;
      maxWidth?: number;
    }>({
      left: 0,
      top: 0,
      maxHeight: 0,
      maxWidth: undefined
    });

    const defaultValue = {
      activeMenuId: undefined,
      setActiveMenuId,
      labelTextWrap,
      selectedValue,
      setSelectedValue(value: string): void {
        setSelectedValue(value);
        if (props.value !== value) {
          props?.onSelect?.(value);
          props?.onClose?.();
          setActiveMenuId(undefined);
        }
      },
      selectedValues,
      setSelectedValues: (values: string[]) => setSelectedValues([...values]),
      isNested,
      setIsNested,
      resetSelections: () => {
        setSelectedValues([]);
      },
      applySelections: () => {
        props?.onSelect?.(selectedValues);
        props?.onClose?.();
      }
    };
    // console.log('selectedvalues', selectedValues);
    // const handleSelect = (value: string) => {
    //   if (props.kind === 'multi-select') {
    //     setSelectedValues((prevSelectedValues) => {
    //       if (prevSelectedValues.includes(value)) {
    //         return prevSelectedValues.filter((v) => v !== value);
    //       }
    //       return [...prevSelectedValues, value];
    //     });
    //   } else {
    //     setSelectedValue(value);
    //     props?.onSelect?.(value);
    //     setMenuVisible(false);
    //     props?.onClose?.();
    //   }
    // };
    // const handleApply = () => {
    //   if (props.kind === 'multi-select') {
    //     props?.onSelect?.(selectedValues);
    //     setActiveMenuId(undefined);
    //     setMenuVisible(false);
    //     props?.onClose?.();
    //   }
    // };

    // position calculation
    useEffect(() => {
      if (props.open) {
        setInternalOpen(true);
        if (dropdownMenuRef && anchorEl?.current) {
          setFullScreen(false);
          setMenuWidth('max-content');
          setTimeout(() => {
            const anchorDomRect = anchorEl.current?.getBoundingClientRect();
            const containerDomRect = dropdownMenuRef?.getBoundingClientRect();

            if (anchorDomRect && containerDomRect) {
              const calculatedPosition = getCalculatedPosition(
                position || 'bottom-start',
                anchorDomRect,
                containerDomRect
              );

              const { left, top, maxHeight, maxWidth } = getPositionCoordinates(
                calculatedPosition,
                anchorDomRect,
                containerDomRect
              );

              const menuItems = dropdownMenuRef.querySelectorAll(
                '.j-JDSDropdownMenuItem-container'
              );

              const isFullScreen = Devices.isMobile()
                ? isNested || (menuItems?.length || 0) > 7
                : false;

              setFullScreen(isFullScreen);
              if (isFullScreen) {
                setCoords({
                  left: 0,
                  top: 0,
                  maxHeight,
                  maxWidth
                });
              } else {
                setCoords({
                  left,
                  top,
                  maxHeight,
                  maxWidth
                });
              }

              setMenuVisible(true);

              if (props.width === 'trigger') {
                setMenuWidth(`${anchorDomRect.width}px`);
              } else {
                setMenuWidth(
                  maxWidth && containerDomRect.width > maxWidth
                    ? `${maxWidth}px`
                    : 'max-content'
                );
              }
            }
          }, 10);
        }
      } else {
        setMenuVisible(false);

        setTimeout(() => {
          setInternalOpen(false);
        }, 300); // --transitions-duration-rapid
      }
    }, [props.open, anchorEl, position, dropdownMenuRef, isNested]);

    useEffect(() => {
      if (props.kind === 'multi-select') {
        setSelectedValues(props.value ? [props.value] : []);
      } else {
        setSelectedValue(props.value);
      }
    }, [props.value]);

    // useEffect(() => {
    //   setSelectedValues(props.value ? [props.value] : []);
    // }, [props.value]);

    useEffect(() => {
      setLabelTextWrap(!!props.labelTextWrap);
    }, [props.labelTextWrap]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (props.open && !activeMenuId) {
          switch (event.key) {
            case 'ArrowUp': {
              handlePrevOrNextItemFocus(
                -1,
                dropdownMenuRef,
                '.j-JDSDropdownMenuItem-container'
              );
              event.preventDefault();
              event.stopPropagation();
              break;
            }
            case 'ArrowDown': {
              handlePrevOrNextItemFocus(
                1,
                dropdownMenuRef,
                '.j-JDSDropdownMenuItem-container'
              );
              event.preventDefault();
              event.stopPropagation();
              break;
            }
            case 'Escape': {
              props?.onClose?.();
              anchorEl?.current?.focus?.();
              break;
            }
          }
        }
      };
      if (props.open) {
        window.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [props.open, dropdownMenuRef, activeMenuId]);

    const shouldShowSuffix = props?.searchConfig?.suffix !== false;
    const close = () => {
      props?.onClose?.();
      setMenuVisible(false);
      setActiveMenuId(undefined);
    };
    useScrollClose(close);
    return (
      <DropdownMenuContext.Provider
        value={{
          ...defaultValue,
          setActiveMenuId,
          setIsNested,
          activeMenuId,
          labelTextWrap,
          selectedValue,
          selectedValues,
          isNested
        }}
      >
        <Component
          {...{
            ...props,
            open: internalOpen,
            onClose: () => {
              props?.onClose?.();
              setMenuVisible(false);
              setActiveMenuId(undefined);
            },
            rootRef,
            offsetX: internalProps.offsetX || `${coords.left}px`,
            _menuWidth: fullScreen ? '100%' : menuWidth,
            _menuFullScreen: fullScreen,
            _menuMaxHeight:
              coords.maxHeight === 0
                ? 'unset'
                : props.searchTrigger
                ? `${coords.maxHeight - SEARCH_BAR_HEIGHT}px`
                : `${coords.maxHeight}px`,
            offsetY: internalProps.offsetY || `${coords.top}px`,
            searchSuffix: shouldShowSuffix ? 'IcMic' : undefined,
            searchLabel:
              props.searchLabel || props?.searchConfig?.search || 'Search',
            searchValue: props.searchValue || props?.searchConfig?.value,
            onSuffixClick:
              props.onSuffixClick || props?.searchConfig?.onSuffixClick,
            onSearchChange: props?.onSearchChange,
            _onContainerClick: (event: MouseEvent) => {
              event.stopPropagation();
            },
            onPrimaryClick: () => {
              props.onPrimaryClick?.();
              defaultValue.applySelections();
            },
            onSecondaryClick: () => {
              props.onSecondaryClick?.();
              defaultValue.resetSelections();
            },
            _menuContainerVisible: menuVisible
          }}
        />
      </DropdownMenuContext.Provider>
    );
  };

export type { DropdownMenuProps };
